/**
 * CapabilitiesContext - Provides plan-based feature gating throughout the app.
 * Fetches capabilities once after authentication and caches them.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { capabilitiesApi, Capability, PlanLimits, PlanUsage, PlanId } from '@/services/capabilitiesApi';
import { getUserIdentityId } from '@/lib/awsClients';
import { createLogger } from '@/lib/logger';

const log = createLogger('CapabilitiesContext');

interface CapabilitiesContextValue {
  /** Whether capabilities are still being fetched */
  isLoading: boolean;
  /** User's plan ID (e.g., 'free', 'starter', 'pro') */
  planId: PlanId;
  /** List of capabilities the user has access to */
  capabilities: string[];
  /** Plan usage limits */
  limits: PlanLimits;
  /** Current usage stats */
  usage: PlanUsage;
  /** Number of recordings remaining today (-1 = unlimited) */
  recordingsRemaining: number;
  /** Check if user has a specific capability */
  hasCapability: (capability: Capability) => boolean;
  /** Check if user is on a paid plan */
  isPaidPlan: boolean;
  /** Check if user is on the Pro plan */
  isProPlan: boolean;
  /** Refresh capabilities from the API */
  refreshCapabilities: () => Promise<void>;
}

const defaultLimits: PlanLimits = {
  recordingsPerDay: 3,
  maxRecordingMinutes: 30,
  cloudStorageGb: 0,
  trialDays: 30,
};

const defaultUsage: PlanUsage = {
  recordingsToday: 0,
};

const CapabilitiesContext = createContext<CapabilitiesContextValue | null>(null);

export const CapabilitiesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();

  const [isLoading, setIsLoading] = useState(true);
  const [planId, setPlanId] = useState<PlanId>('free');
  const [capabilities, setCapabilities] = useState<string[]>(['record', 'apply_template', 'create_project']);
  const [limits, setLimits] = useState<PlanLimits>(defaultLimits);
  const [usage, setUsage] = useState<PlanUsage>(defaultUsage);

  // Fetch capabilities when authentication state changes
  const fetchCapabilities = useCallback(async () => {
    if (!isAuthenticated) {
      // Reset to default capabilities for unauthenticated users
      setPlanId('free');
      setCapabilities(['record', 'apply_template', 'create_project']);
      setLimits(defaultLimits);
      setUsage(defaultUsage);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Resolve identityId for accurate job count (jobs are keyed by identityId)
      let identityId: string | undefined;
      try {
        identityId = await getUserIdentityId();
      } catch {
        // Proceed without identityId — backend falls back to cognito sub
      }

      const data = await capabilitiesApi.getUserCapabilities(identityId);
      setPlanId(data.planId as PlanId);
      setCapabilities(data.capabilities);
      setLimits(data.limits);
      setUsage(data.usage || defaultUsage);
      log.info('Capabilities loaded', { planId: data.planId, capCount: data.capabilities.length });
    } catch (error) {
      log.error('Failed to fetch capabilities', error instanceof Error ? error : new Error(String(error)));
      // Keep existing capabilities on error
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch capabilities after auth is ready
  useEffect(() => {
    if (!authLoading) {
      fetchCapabilities();
    }
  }, [authLoading, isAuthenticated, fetchCapabilities]);

  // Auto-refresh capabilities at UTC midnight so daily limits reset
  useEffect(() => {
    if (!isAuthenticated) return;

    const scheduleNextMidnight = () => {
      const now = new Date();
      const nextMidnight = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0, 0, 5 // 5 seconds after midnight for safety
      ));
      const ms = nextMidnight.getTime() - now.getTime();

      return setTimeout(() => {
        log.info('UTC midnight reached — refreshing capabilities');
        fetchCapabilities();
        // Schedule the next midnight refresh
        timerRef.current = scheduleNextMidnight();
      }, ms);
    };

    const timerRef = { current: scheduleNextMidnight() };

    return () => clearTimeout(timerRef.current);
  }, [isAuthenticated, fetchCapabilities]);

  // Check if user has a specific capability
  const hasCapability = useCallback(
    (capability: Capability): boolean => {
      return capabilities.includes(capability);
    },
    [capabilities]
  );

  const recordingsRemaining = useMemo(() => {
    if (limits.recordingsPerDay === -1) return -1;
    return Math.max(0, limits.recordingsPerDay - usage.recordingsToday);
  }, [limits.recordingsPerDay, usage.recordingsToday]);

  const value = useMemo<CapabilitiesContextValue>(
    () => ({
      isLoading: isLoading || authLoading,
      planId,
      capabilities,
      limits,
      usage,
      recordingsRemaining,
      hasCapability,
      isPaidPlan: planId === 'starter' || planId === 'pro',
      isProPlan: planId === 'pro',
      refreshCapabilities: fetchCapabilities,
    }),
    [isLoading, authLoading, planId, capabilities, limits, usage, recordingsRemaining, hasCapability, fetchCapabilities]
  );

  return (
    <CapabilitiesContext.Provider value={value}>
      {children}
    </CapabilitiesContext.Provider>
  );
};

export function useCapabilities(): CapabilitiesContextValue {
  const context = useContext(CapabilitiesContext);
  if (!context) {
    throw new Error('useCapabilities must be used within a CapabilitiesProvider');
  }
  return context;
}

export default CapabilitiesContext;
